/**
 * Platform · who is told.
 *
 * The rules that send work orders out. Each reads as a sentence — when this
 * kind of work reaches this point, tell these people this way — and shows
 * who it actually reaches, at which numbers and addresses, from the contact
 * directory. The platform applies the rules the moment a work order is raised
 * or changes state; an "overdue" rule fires once when the work passes its due
 * time, and an escalation fires once when nobody has started the work within
 * its window.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useDirectory, useDispatchGroups, useNotifyRules, useReach, useSaveNotifyRule } from "../../api/queries";
import { Button, Card, EmptyState, ErrorState, Field, Input, Skeleton } from "../../design/components";
import {
  CHANNEL_LABEL,
  EVENT_LABEL,
  EVENTS,
  type Channel,
  type NotifyRule,
  type Recipient,
} from "../../domain/contacts";
import { WORK_ORDER_KINDS } from "../../domain/workOrders";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { Dialog } from "../stores/Purchasing";
import s from "./Contacts.module.css";

export function NotifyRulesPage() {
  const session = useSession();
  const rules = useNotifyRules();
  const names = useRecipientNames();
  const save = useSaveNotifyRule();
  const [edit, setEdit] = useState<NotifyRule | null>(null);

  useScreenContext({
    route: "/notify-rules",
    title: "Who is told",
    summary: [t("{n} rules decide who is told about which work.", { n: rules.data?.length ?? 0 })],
    offers: [{ label: t("Who is told about P1 work?"), ask: "who is told when a P1 work order is raised?" }],
    worksInFull: true,
  });

  const blank: NotifyRule = {
    siteId: session.site.id,
    name: "",
    active: true,
    events: ["created"],
    match: {},
    recipients: [],
    channels: ["sms", "email", "app"],
    escalateTo: [],
  };

  return (
    <Page eyebrow={t("{site} · Platform", { site: session.site.code })} title="Who is told">
      <p className={s.lede}>
        {t(
          "Rules that send work orders by SMS, email and the app to people in the contact directory.",
        )}{" "}
        <Link to="/contacts">{t("Contact directory →")}</Link>
      </p>
      <div className={s.bar}>
        <Button variant="primary" onClick={() => setEdit(blank)} data-testid="rule-add">
          {t("Add a rule")}
        </Button>
      </div>
      {rules.isPending ? (
        <Skeleton rows={5} />
      ) : rules.isError ? (
        <ErrorState error={rules.error} retry={() => void rules.refetch()} />
      ) : rules.data.length === 0 ? (
        <EmptyState>
          {t("No rules yet. Only teams dispatched from the map hear about work orders.")}
        </EmptyState>
      ) : (
        <div className={s.rules}>
          {rules.data.map((r) => (
            <Card
              key={r.id}
              title={r.name}
              sub={r.active ? t("on") : t("switched off")}
              testId={`rule-${r.name}`}
              actions={
                <div className={s.actions}>
                  <Button size="sm" onClick={() => save.mutate({ ...r, active: !r.active })}>
                    {r.active ? t("Switch off") : t("Switch on")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(structuredClone(r))}>
                    {t("Edit")}
                  </Button>
                </div>
              }
            >
              <div className={s.rule} data-active={r.active}>
                <p className={s.ruleLine}>{ruleSentence(r, names)}</p>
                <Reaches recipients={r.recipients} />
              </div>
            </Card>
          ))}
        </div>
      )}
      {edit && <RuleDialog rule={edit} onClose={() => setEdit(null)} />}
    </Page>
  );
}

function useRecipientNames() {
  const dir = useDirectory();
  const groups = useDispatchGroups();
  return (r: Recipient): string => {
    if (r.type === "org") return dir.data?.orgs.find((o) => o.id === r.id)?.name ?? r.id;
    if (r.type === "group") return t("team {name}", { name: groups.data?.find((g) => g.key === r.id)?.name ?? r.id });
    const p = [...(dir.data?.orgs.flatMap((o) => o.people) ?? []), ...(dir.data?.people ?? [])].find(
      (x) => x.id === r.id,
    );
    return p?.name ?? r.id;
  };
}

function ruleSentence(r: NotifyRule, name: (x: Recipient) => string): string {
  const what = [
    r.match.priorities?.length ? r.match.priorities.map((p) => `P${p}`).join("/") : null,
    r.match.kinds?.length ? r.match.kinds.join("/") : null,
    r.match.dispatchGroups?.length
      ? t("sent to {teams}", { teams: r.match.dispatchGroups.map((k) => name({ type: "group", id: k })).join(", ") })
      : null,
    r.match.zoneCodes?.length ? t("in {zones}", { zones: r.match.zoneCodes.join(", ") }) : null,
  ]
    .filter(Boolean)
    .join(" ");
  const line = t("When {what} work is {events}, tell {who} by {how}.", {
    what: what || t("any"),
    events: r.events.map((e) => t(EVENT_LABEL[e]).toLowerCase()).join(", "),
    who: r.recipients.map(name).join(", ") || "—",
    how: r.channels.map((c) => t(CHANNEL_LABEL[c])).join(", "),
  });
  return r.escalateAfterMin
    ? `${line} ${t("If nobody starts it within {m} minutes, tell {who}.", { m: r.escalateAfterMin, who: r.escalateTo.map(name).join(", ") })}`
    : line;
}

function Reaches({ recipients }: { recipients: Recipient[] }) {
  const reach = useReach(recipients);
  if (!recipients.length) return null;
  if (reach.isPending) return <Skeleton rows={1} />;
  return (
    <div className={s.reach} data-testid="reach">
      {(reach.data ?? []).map((p, i) => (
        <div key={i}>
          <b>{p.name}</b>
          <span>📱 {p.sms.length ? p.sms.join(", ") : t("no mobile")}</span>
          <span>✉ {p.email.length ? p.email.join(", ") : t("no email")}</span>
          <span className={p.app ? s.tagOn : s.tag}>{p.app ? t("mobile app") : t("not on the app")}</span>
        </div>
      ))}
    </div>
  );
}

function Chips<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { v: T; label: string }[];
  value: T[];
  onChange: (v: T[]) => void;
  label: string;
}) {
  return (
    <div className={s.chips} role="group" aria-label={t(label)}>
      {options.map((o) => (
        <button
          key={String(o.v)}
          type="button"
          className={s.chip}
          aria-pressed={value.includes(o.v)}
          onClick={() => onChange(value.includes(o.v) ? value.filter((x) => x !== o.v) : [...value, o.v])}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RecipientPicker({
  value,
  onChange,
  label,
}: {
  value: Recipient[];
  onChange: (v: Recipient[]) => void;
  label: string;
}) {
  const dir = useDirectory();
  const groups = useDispatchGroups();
  const has = (r: Recipient) => value.some((x) => x.type === r.type && x.id === r.id);
  const toggle = (r: Recipient) =>
    onChange(has(r) ? value.filter((x) => !(x.type === r.type && x.id === r.id)) : [...value, r]);
  const people = [
    ...(dir.data?.orgs.flatMap((o) => o.people.map((p) => ({ p, org: o.name }))) ?? []),
    ...(dir.data?.people ?? []).map((p) => ({ p, org: "" })),
  ];
  return (
    <div className={s.editor} aria-label={t(label)}>
      <div>
        <div className={s.dim}>{t("Organizations — everyone in them")}</div>
        <div className={s.chips}>
          {(dir.data?.orgs ?? []).map((o) => (
            <button
              key={o.id}
              type="button"
              className={s.chip}
              aria-pressed={has({ type: "org", id: o.id! })}
              onClick={() => toggle({ type: "org", id: o.id! })}
            >
              🏢 {o.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className={s.dim}>{t("People")}</div>
        <div className={s.chips}>
          {people.map(({ p, org }) => (
            <button
              key={p.id}
              type="button"
              className={s.chip}
              aria-pressed={has({ type: "person", id: p.id! })}
              onClick={() => toggle({ type: "person", id: p.id! })}
            >
              👤 {p.name}
              {org && <small className={s.dim}> · {org}</small>}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className={s.dim}>{t("Dispatch teams")}</div>
        <div className={s.chips}>
          {(groups.data ?? []).map((g) => (
            <button
              key={g.key}
              type="button"
              className={s.chip}
              aria-pressed={has({ type: "group", id: g.key })}
              onClick={() => toggle({ type: "group", id: g.key })}
            >
              🚒 {g.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function RuleDialog({ rule, onClose }: { rule: NotifyRule; onClose: () => void }) {
  const save = useSaveNotifyRule();
  const groups = useDispatchGroups();
  const [v, setV] = useState(rule);
  const [zones, setZones] = useState((rule.match.zoneCodes ?? []).join(", "));
  const [esc, setEsc] = useState(!!rule.escalateAfterMin);
  const out: NotifyRule = {
    ...v,
    match: {
      ...v.match,
      zoneCodes: zones
        .split(",")
        .map((z) => z.trim())
        .filter(Boolean),
    },
    escalateAfterMin: esc ? (v.escalateAfterMin ?? 15) : undefined,
    escalateTo: esc ? v.escalateTo : [],
  };
  const ready =
    v.name.trim() && v.events.length && v.recipients.length && v.channels.length && (!esc || v.escalateTo.length);
  return (
    <Dialog title={rule.id ? t("Edit the rule") : t("Add a rule")} onClose={onClose} testId="rule-dialog">
      <div className={s.editor}>
        <Field label="Name">
          <Input
            value={v.name}
            onChange={(e) => setV({ ...v, name: e.target.value })}
            aria-label={t("Rule name")}
            placeholder={t("e.g. Lighting faults — the AGL contractor")}
          />
        </Field>
        <h3>{t("When")}</h3>
        <Chips
          label="Events"
          options={EVENTS.map((e) => ({ v: e, label: t(EVENT_LABEL[e]) }))}
          value={v.events}
          onChange={(events) => setV({ ...v, events })}
        />
        <h3>{t("Which work")}</h3>
        <Chips
          label="Priorities"
          options={[1, 2, 3, 4, 5].map((p) => ({ v: p, label: `P${p}` }))}
          value={v.match.priorities ?? []}
          onChange={(priorities) => setV({ ...v, match: { ...v.match, priorities } })}
        />
        <Chips
          label="Kinds"
          options={WORK_ORDER_KINDS.map((k) => ({ v: k as string, label: t(k.replace("_", " ")) }))}
          value={v.match.kinds ?? []}
          onChange={(kinds) => setV({ ...v, match: { ...v.match, kinds } })}
        />
        <Chips
          label="Dispatched to"
          options={(groups.data ?? []).map((g) => ({ v: g.key, label: g.name }))}
          value={v.match.dispatchGroups ?? []}
          onChange={(dispatchGroups) => setV({ ...v, match: { ...v.match, dispatchGroups } })}
        />
        <Field label="Zones (codes, comma separated; a code covers everything that starts with it)">
          <Input value={zones} onChange={(e) => setZones(e.target.value)} placeholder="RWY, TWY-B, APRON-1" />
        </Field>
        <p className={s.dim}>{t("Nothing chosen under Which work means every work order.")}</p>
        <h3>{t("Tell")}</h3>
        <RecipientPicker
          label="Recipients"
          value={v.recipients}
          onChange={(recipients) => setV({ ...v, recipients })}
        />
        <Reaches recipients={v.recipients} />
        <h3>{t("How")}</h3>
        <Chips
          label="Channels"
          options={(Object.keys(CHANNEL_LABEL) as Channel[]).map((c) => ({ v: c, label: t(CHANNEL_LABEL[c]) }))}
          value={v.channels}
          onChange={(channels) => setV({ ...v, channels })}
        />
        <label className={s.check}>
          <input type="checkbox" checked={esc} onChange={(e) => setEsc(e.target.checked)} />{" "}
          {t("If nobody starts it in time, tell someone else")}
        </label>
        {esc && (
          <>
            <Field label="Within (minutes)">
              <Input
                type="number"
                min={5}
                max={1440}
                value={v.escalateAfterMin ?? 15}
                onChange={(e) => setV({ ...v, escalateAfterMin: Number(e.target.value) })}
              />
            </Field>
            <RecipientPicker
              label="Escalate to"
              value={v.escalateTo}
              onChange={(escalateTo) => setV({ ...v, escalateTo })}
            />
          </>
        )}
        {save.isError && <p className={s.bad}>{String(save.error)}</p>}
        <div className={s.actions}>
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button
            variant="primary"
            disabled={!ready || save.isPending}
            onClick={() => save.mutate(out, { onSuccess: onClose })}
            data-testid="rule-save"
          >
            {t("Save")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
